import { useCallback, useEffect, useMemo, useRef } from 'react';

import { usePersonaInitPersonaSession, usePersonaRespondWithPersonaAudio } from '@/api-gen/api.ts';
import type { Gender, TtsProvider } from '@/api-gen/api.schemas.ts';
import { vadThreshold, silenceThreshold, smartTurnEnabled } from '@/features/audio/state/signals.ts';
import {
  personaChatUiOpen,
  personaChatSessionId,
  personaChatState,
  personaChatIsRecording,
  personaChatIsLoading,
  personaChatIsResponding,
  personaChatError,
  personaChatLastUserText,
  personaChatLastAiText,
  personaChatLastUserAt,
  personaChatLastAiAt,
  resetPersonaChatSignals
} from '@/features/personaChat/state/signals.ts';
import { useWebsocketAudioModule } from 'app/hooks/useWebSocketAudioModule.ts';
import logger from 'app/utils/logger.ts';

interface PersonaChatConfig {
  gender?: Gender;
  name?: string | null;
  languageCode?: string | null;
  ttsProvider?: TtsProvider;
  voiceId?: string | null;
  description?: string | null;
}

type PlayAudioCallback = (base64Data: string | null | undefined) => Promise<void>;

const decodeBase64ToArrayBuffer = (base64Data: string) => {
  const binaryString = atob(base64Data);
  const len = binaryString.length;
  if (len < 44) {
    throw new Error('Audio payload too small.');
  }
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

export const usePersonaChat = (config: PersonaChatConfig) => {
  const initPersonaSessionMutation = usePersonaInitPersonaSession();
  const respondWithPersonaMutation = usePersonaRespondWithPersonaAudio();

  const chatState = personaChatState.value;

  const audioContextRef = useRef<AudioContext | null>(null);
  const audioSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const sessionPromiseRef = useRef<Promise<string> | null>(null);

  const ensureAudioContext = useCallback(async () => {
    let currentContext = audioContextRef.current;
    if (!currentContext) {
      currentContext = new AudioContext();
      audioContextRef.current = currentContext;
    }
    if (currentContext.state === 'suspended') {
      await currentContext.resume();
    }
    return currentContext;
  }, []);

  const stopActivePlayback = useCallback(() => {
    const currentSource = audioSourceRef.current;
    if (currentSource) {
      try {
        currentSource.stop();
      } catch (error) {
        logger.warn('[usePersonaChat] Failed stopping audio source', error);
      }
      audioSourceRef.current = null;
    }
  }, []);

  const playBase64Audio: PlayAudioCallback = useCallback(async (base64Data) => {
    if (!base64Data || base64Data.trim().length === 0) {
      personaChatState.value = 'idle';
      return;
    }
    try {
      const arrayBuffer = decodeBase64ToArrayBuffer(base64Data);
      const audioContext = await ensureAudioContext();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0));

      stopActivePlayback();

      const source = audioContext.createBufferSource();
      audioSourceRef.current = source;
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);
      personaChatState.value = 'speaking';
      source.onended = () => {
        if (personaChatState.value === 'speaking') {
          personaChatState.value = 'idle';
        }
        audioSourceRef.current = null;
      };
      source.start(0);
    } catch (error) {
      logger.error('[usePersonaChat] Failed to play persona audio', error);
      personaChatError.value = 'Unable to play persona audio.';
      personaChatState.value = 'idle';
    }
  }, [ensureAudioContext, stopActivePlayback]);

  const stopAudioGraph = useCallback(() => {
    stopActivePlayback();
    const ctx = audioContextRef.current;
    if (ctx) {
      ctx.close().catch((error) => {
        logger.warn('[usePersonaChat] Failed closing AudioContext', error);
      });
      audioContextRef.current = null;
    }
  }, [stopActivePlayback]);

  const readyToListen = useMemo(() => (chatState === 'idle' || chatState === 'listening'), [chatState]);

  const audioModule = useWebsocketAudioModule({
    wsUrl: process.env.REACT_APP_VAD_WS,
    serverType: 'vad',
    mode: 'both',
    vadThreshold: vadThreshold.value,
    silenceThreshold: silenceThreshold.value,
    smartTurnEnabled: smartTurnEnabled.value,
    readyToListen,
    lang: config.languageCode ?? 'auto',
    onSpeechStart: () => {
      personaChatState.value = 'listening';
    },
    onSpeechEnd: () => {
      personaChatState.value = 'transcribing';
    },
    onWavAndTranscriptionClip: async (_base64Data, text) => {
      if (!personaChatIsRecording.value) {
        personaChatState.value = 'idle';
        return;
      }
      if (!text || text.trim().length === 0) {
        personaChatState.value = 'idle';
        return;
      }
      // Update last user text for UI preview
      personaChatLastUserText.value = text;
      personaChatLastUserAt.value = Date.now();
      personaChatIsResponding.value = true;
      try {
        let currentSessionId = personaChatSessionId.value;
        if (!currentSessionId && sessionPromiseRef.current) {
          currentSessionId = await sessionPromiseRef.current;
        }
        if (!currentSessionId) {
          throw new Error('Persona session is not ready.');
        }
        const response = await respondWithPersonaMutation.mutateAsync({
          data: {
            sessionId: currentSessionId,
            text
          }
        });
        // Update last AI text (if provided by API)
        const aiText = (response?.data?.text ?? '').trim();
        if (aiText.length > 0) {
          personaChatLastAiText.value = aiText;
          personaChatLastAiAt.value = Date.now();
        }
        const audio = response?.data?.audio ?? '';
        await playBase64Audio(audio);
      } catch (error) {
        logger.error('[usePersonaChat] Failed responding with persona audio', error);
        personaChatError.value = 'Persona response failed.';
        personaChatState.value = 'idle';
      } finally {
        personaChatIsResponding.value = false;
      }
    },
    onTranscriptionFailed: () => {
      personaChatState.value = 'idle';
      personaChatIsResponding.value = false;
      personaChatError.value = 'We could not transcribe your speech. Please try again.';
    }
  });

  const stopChat = useCallback(() => {
    stopActivePlayback();
    audioModule.stop();
    personaChatIsRecording.value = false;
    personaChatIsResponding.value = false;
    personaChatIsLoading.value = false;
    personaChatState.value = 'idle';
    personaChatSessionId.value = '';
    personaChatLastUserText.value = null;
    personaChatLastAiText.value = null;
    personaChatLastUserAt.value = null;
    personaChatLastAiAt.value = null;
  }, [audioModule, stopActivePlayback]);

  const closeChat = useCallback(() => {
    stopChat();
    stopAudioGraph();
    sessionPromiseRef.current = null;
    resetPersonaChatSignals();
  }, [stopChat, stopAudioGraph]);

  const startChat = useCallback(async () => {
    if (personaChatIsLoading.value || personaChatIsResponding.value) {
      return;
    }
    personaChatUiOpen.value = true;
    personaChatError.value = null;
    personaChatIsLoading.value = true;
    personaChatState.value = 'starting';
    // Reset last exchange preview when starting a new session
    personaChatLastUserText.value = null;
    personaChatLastAiText.value = null;
    personaChatLastUserAt.value = null;
    personaChatLastAiAt.value = null;

    const payload = {
      gender: config.gender,
      name: config.name ?? null,
      language: config.languageCode ?? null,
      ttsProvider: config.ttsProvider,
      voiceId: config.voiceId ?? null,
      description: config.description ?? null
    };

    const sessionPromise = (async () => {
      try {
        const response = await initPersonaSessionMutation.mutateAsync({ data: payload });
        const body = response?.data ?? (response as any)?.data;
        const sessionId = body?.sessionId ?? '';
        if (!sessionId) {
          throw new Error('Persona session did not return an id.');
        }
        personaChatSessionId.value = sessionId;
        // If backend provides greeting text, surface it
        const greetingText = (body?.text ?? '').trim();
        if (greetingText.length > 0) {
          personaChatLastAiText.value = greetingText;
          personaChatLastAiAt.value = Date.now();
        }
        if (body?.audio) {
          await playBase64Audio(body.audio);
        } else if (personaChatState.value === 'starting') {
          personaChatState.value = 'idle';
        }
        return sessionId;
      } catch (error) {
        logger.error('[usePersonaChat] Failed starting persona session', error);
        personaChatError.value = 'Unable to start persona chat.';
        personaChatState.value = 'idle';
        throw error;
      }
    })();
    sessionPromiseRef.current = sessionPromise;

    try {
      await audioModule.start();
      personaChatIsRecording.value = true;
    } catch (error) {
      logger.error('[usePersonaChat] Failed starting audio module', error);
      personaChatError.value = 'Microphone access is required for persona chat.';
      stopChat();
      stopAudioGraph();
      personaChatUiOpen.value = false;
      sessionPromiseRef.current = null;
      return;
    }

    try {
      const sessionId = await sessionPromise;
      if (!sessionId) {
        throw new Error('Persona session id missing after init.');
      }
      if (personaChatState.value === 'starting') {
        personaChatState.value = 'idle';
      }
    } catch (error) {
      logger.error('[usePersonaChat] Persona session failed to initialize', error);
      personaChatError.value = 'Unable to start persona chat.';
      stopChat();
      stopAudioGraph();
      personaChatUiOpen.value = false;
      return;
    } finally {
      personaChatIsLoading.value = false;
    }
  }, [audioModule, closeChat, config, initPersonaSessionMutation, playBase64Audio]);

  // Only clean up on unmount to avoid stopping the chat during normal re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    return () => {
      try {
        closeChat();
      } catch (error) {
        logger.warn('[usePersonaChat] Cleanup on unmount failed', error);
      }
    };
  }, []);

  return {
    startChat,
    stopChat,
    closeChat,
    isOpen: personaChatUiOpen.value,
    state: personaChatState.value,
    isRecording: personaChatIsRecording.value,
    isLoading: personaChatIsLoading.value,
    isResponding: personaChatIsResponding.value,
    error: personaChatError.value
  };
};

export type UsePersonaChatReturn = ReturnType<typeof usePersonaChat>;
